/**
 * This is our implementation of of affinity after electron has removed support in electron 14.
 *
 * Child windows launched with window.open from a shared parent will share the same process as long as they exist on the same domain.
 *
 * Each time a window with a new affinity is launched, we create an AffinitySurrogate - a hidden window with minimal content - to manage
 * creation of all child windows (childWindows). AffinitySurrogates are launched on the same domain as finsemble so the Surrogates for our services
 * will end up on the same as process and save memory.
 *
 * We don't need to keep track of the domain of the window launched. While windows on different domains will end up in different
 * processes, for our tracking purposes that doesn't matter.
 *
 * If we have an affinity "workspacecomponents", with two children at google.com and two on the finsemble domain,
 * all four windows will share the same affinity, but two will be in one process and two in another.
 *
 * These groupings will show in process monitor, but we can still manipulate all four windows here.
 */

import { BrowserWindow, DidCreateWindowDetails, WebContents } from "electron";
import { BrowserWindowConstructorOptions } from "electron/main";
import { windowsIndex } from "./mainStore";
import { MainWindow } from "./MainWindow";
import { getOrCreateMainLogger } from "../logger/main";
import uuidv4 from "uuid-random";

const AFFINITY_CHILD_ID_KEY = "_affinityChildID";

type AffinityBrowserWindowConstructorOptions = BrowserWindowConstructorOptions & {
	[AFFINITY_CHILD_ID_KEY]: string;
};

const logger = getOrCreateMainLogger();

/**
 * How long to wait before closing an AffinitySurrogate window when all children have been closed.
 * This wait is needed to stop affinities closing during workspace switches when the same affinity is used in both the old and now workspace.
 */
const AFFINITY_GROUP_CLOSE_DELAY = 10000;

/**
 * private
 * Information for each child window we store to handle spawn and close
 * url - child window url
 * pending - Starts as true and is changed to false after child window is spawned
 * browserWindow - browserWindow object of the child window
 * opts - A set of BrowserWindowConstructorOptions to pass to the BrowserWindow constructor
 * callback - A function to be called once the child BrowserWindow is created under the given affinity
 */
type ChildWindow = {
	id: string;
	url: string;
	pending: boolean;
	browserWindow: BrowserWindow | null;
	opts: BrowserWindowConstructorOptions;
	callback: (browserWindow: BrowserWindow) => void;
};

/**
 * An AffinitySurrogate is a hidden window representing a named render process affinity that handles
 * spawning child windows with window.open.
 *
 * We capture each window.open and inject the corresponding window settings via `setWindowOpenHandler`
 *
 * We capture the created webcontents via the "did-create-window" event.
 *
 * The Chromium engine will render any child windows under the same PID, so long as they're served
 * from the same 'site' (domain and protocol, ignoring subdomain, port and path).
 *
 * Child windows, represented by the ChildWindow type, are tracked by the AffinitySurrogate,
 * allowing the AffinitySurrogate window to close when no more child windows exist.
 */
class AffinitySurrogate {
	/**
	 * The name of the affinity group
	 */
	private id;

	/**
	 * AffinitySurrogate loadURL completed
	 */
	private didInitialize: boolean = false;

	/**
	 * Callbacks to be called once AffinitySurrogate is initialized
	 */
	private onInitializedCallbacks: Function[] = [];

	/**
	 * The hidden window from which we will `window.open` child windows
	 */
	browserWindow: BrowserWindow;

	/**
	 * A store of child windows.
	 *
	 * Each child window either has a BrowserWindow or is pending (awaiting a BrowserWindow)
	 */
	childWindows: ChildWindow[] = [];

	/**
	 * Called when the AffinitySurrogate no longer has any child windows.
	 *
	 * The empty check happens after a timeout defined by AFFINITY_GROUP_CLOSE_DELAY
	 */
	onEmpty: (() => void) | null = null;

	/**
	 * Timeout used by onEmpty. Duration defined by AFFINITY_GROUP_CLOSE_DELAY.
	 */
	checkEmptyTimeout: ReturnType<typeof setTimeout> | null = null;

	/**
	 * @param affinityID Affinity group name to use
	 * @param surrogateURL Optional URL for the surrogate to load. Loading a URL puts the surrogate on a
	 * particular 'site' allowing its own render process to group with other windows on the same 'site'.
	 */
	constructor(affinityID: string) {
		const windowTitle = `Affinity Group: ${affinityID}`;

		this.id = affinityID;

		logger.log(`Constructing Affinity surrogate: ${windowTitle}.`);

		this.browserWindow = new BrowserWindow({
			webPreferences: { contextIsolation: false },
			title: windowTitle,
			show: false,
		});

		this.setupWebContents(this.browserWindow.webContents, windowTitle);
		this.setupListeners();
	}

	async init() {
		return new Promise<void>(async (resolve, reject) => {
			let surrogateUrl = AffinityManager.getSurrogateUrl();
			logger.log(`AffinitySurrogate ${this.id} loading URL: ${surrogateUrl}`);
			if (surrogateUrl) {
				try {
					// The affinity surrogates load an almost empty html file on the finsemble domain instead of loading about:blank
					// or not loading a url. This allows the surrogates for our services to share the same pid as their children saving memory.
					await this.browserWindow.loadURL(surrogateUrl);
					logger.log(`AffinitySurrogate ${this.id} successfully loaded URL: ${surrogateUrl}`);
					this.didInitialize = true;
					this.onInitializedCallbacks.forEach((callback) => callback());
					resolve();
				} catch (err) {
					logger.error(
						`Failed loading url ${surrogateUrl} for AffinitySurrogate ${this.id}, affinity surrogate will not load a url.`,
						err
					);
					reject();
				}
			} else {
				logger.log(`AffinitySurrogate ${this.id} NOT loading a URL`);
			}
		});
	}

	setupWebContents(webContents: WebContents, windowTitle: string) {
		logger.debug(`AffinitySurrogate ${this.id} setting up webcontents`);
		// Prevent the maxlistener error which can occur if more than 10 child windows are added to an affinity group.
		webContents.setMaxListeners(50);

		// For use in ProcessMonitor component
		windowsIndex.byWebContentsId[webContents.id] = {
			webContentsName: windowTitle,
			webContents,
			mainWindow: new MainWindow({ name: windowTitle, isAffinitySurrogate: true }, {}),
		};
	}

	setupListeners() {
		logger.debug(`AffinitySurrogate ${this.id} setting up listeners`);
		// window.open handler for when an affinity group spawns a child window
		// window.open is only called internally for an affinity group, so groups should not listen
		// for standard window.open call
		this.browserWindow.webContents.setWindowOpenHandler((details: Electron.HandlerDetails) => {
			logger.verbose(`AffinitySurrogate received child window setWindowOpenHandler for URL ${details.url}`);

			const { features } = details;

			if (features.indexOf(AFFINITY_CHILD_ID_KEY) === -1) {
				logger.error(
					`Looks like the affinity child ID key "${AFFINITY_CHILD_ID_KEY}" was not found in the window.open \`features\` string provided in the \`setWindowOpenHandler\` callback. Is electron still allowing us to overload the window.open \`features\` string?`
				);
				return { action: "deny" };
			}

			const child = this.getPendingChildWindow(features);

			if (!child) {
				logger.error(`AffinitySurrogate has no pending child window options for setWindowOpenHandler, returning deny`);
				return { action: "deny" };
			}

			child.pending = false;

			return {
				action: "allow",
				overrideBrowserWindowOptions: child.opts,
			};
		});

		// Finishes spawning child windows. Spawning child windows is queued so the mapping of browserWindow to it's affinity window
		// does not get mixed up when two windows are spawned at the same time.
		this.browserWindow.webContents.on("did-create-window", (bw: BrowserWindow, details: DidCreateWindowDetails) => {
			if (!details.options.hasOwnProperty(AFFINITY_CHILD_ID_KEY)) {
				logger.error(
					`Looks like the affinity child ID key "${AFFINITY_CHILD_ID_KEY}" was not found in the \`details.options\` object provided by the "did-create-window" hook. Is electron still allowing us to overload \`overrideBrowserWindowOptions\`?`
				);
				return;
			}
			logger.debug(`AffinitySurrogate ${this.id} did-create-window for ${details.url}`);

			const childID = (details.options as AffinityBrowserWindowConstructorOptions)[AFFINITY_CHILD_ID_KEY];

			const childWindow = this.childWindows.find((child) => child.id === childID);

			if (!childWindow) {
				logger.error("Attempting to create affinity window before previous affinity window is done being created");
				return;
			}

			childWindow.browserWindow = bw;
			childWindow.callback(childWindow.browserWindow);

			bw.on("closed", () => {
				this.removeChildWindow(bw);

				if (this.checkEmptyTimeout) {
					clearTimeout(this.checkEmptyTimeout);
				}

				/**
				 * Delay declaring an empty affinity surrogate.
				 * We could be switching workspaces in which case the affinity surrogate will likely be reused.
				 */
				this.checkEmptyTimeout = setTimeout(() => {
					if (this.childWindows.length < 1 && this.onEmpty !== null) {
						this.onEmpty();
					}
				}, AFFINITY_GROUP_CLOSE_DELAY);
			});

			logger.debug(`created webcontents for ${details.url} with pid: ${bw.webContents.getOSProcessId()}`);
		});

		this.browserWindow.webContents.on("did-fail-load", (_event, errorCode, errorDescription, validatedURL) => {
			logger.error(`AffinitySurrogate ${this.id} did-fail-load`, errorCode, errorDescription, validatedURL);
		});

		this.browserWindow.webContents.on(
			"did-fail-provisional-load",
			(_event, errorCode, errorDescription, validatedURL) => {
				logger.error(
					`AffinitySurrogate ${this.id} did-fail-provisional-load`,
					errorCode,
					errorDescription,
					validatedURL
				);
			}
		);

		this.browserWindow.webContents.on("certificate-error", (_event, url, error) => {
			logger.error(`AffinitySurrogate ${this.id} certificate-error`, url, error);
		});
	}

	removeChildWindow(bw: BrowserWindow) {
		this.childWindows = this.childWindows.filter((childWindow) => childWindow.browserWindow != bw);
	}

	async onInitialized() {
		return new Promise<void>((resolve) => {
			if (this.didInitialize) {
				resolve();
			} else {
				this.onInitializedCallbacks.push(resolve);
			}
		});
	}

	/**
	 * Initiate spawning a child window using window.open. child windows are stored in an object
	 * so the pending window can be retrieved in the window.open handlers on the affinity group.
	 *
	 * Internally we store windows in a queue so that multiple synchronous calls to launchChildWindow can
	 * spawn child windows for each affinity group all at once.
	 *
	 * Each call to launchChildWindow will resolve after a BrowserWindow is created under the corresponding
	 * URL and affinity group name.
	 */
	async launchChildWindow(url: string, opts: BrowserWindowConstructorOptions): Promise<BrowserWindow> {
		return new Promise(async (resolve, reject) => {
			const child = { id: uuidv4(), url, opts, callback: resolve, pending: true, browserWindow: null };

			this.childWindows.push(child);

			logger.log(`launch child window: child url: ${url}, affinity id: ${this.id}`);

			try {
				await this.onInitialized();
				logger.debug(`launchChildWindow() successfully initialized: child url: ${url}, affinity id: ${this.id}`);

				const target = "_blank";
				const features = `${AFFINITY_CHILD_ID_KEY}=${child.id}`;
				const result = await this.browserWindow.webContents.executeJavaScript(
					`!!window.open("${url}", "${target}", "${features}");`
				);
				logger.debug(`launchChildWindow() JavaScript executed: child url: ${url}, affinity id: ${this.id}`);

				if (result === false) {
					throw `AffinitySurrogate: window.open failed for ${url}`;
				}
			} catch (err) {
				logger.error(`Error launching window from surrogate ${url} ${this.id}`, err);
				reject();
			}
		});
	}

	/**
	 * Returns a pending child window whose ID is contained within the given window.open "features" string.
	 */
	getPendingChildWindow(features: string) {
		return this.childWindows.find(
			(child) => child.pending && features.indexOf(`${AFFINITY_CHILD_ID_KEY}=${child.id}`) !== -1
		);
	}
}

/**
 * Manages the spawning of windows under a specified affinity group.
 *
 * Internally manages the lifecycle and cleanup of each AffinitySurrogate, which represents named affinity group.
 */
export class AffinityManager {
	private affinitySurrogateInstances: { [affinityID: string]: AffinitySurrogate } = {};

	private static surrogateURL: string | null = null;

	constructor() {}

	public static getSurrogateUrl() {
		return AffinityManager.surrogateURL;
	}

	public static setSurrogateUrl(url: string) {
		AffinityManager.surrogateURL = url;
	}

	/**
	 *  Private
	 *  Given a specific affinity value, find a corresponding affinity surrogate if it exists. If not, then create one.
	 * @param affinityID
	 */
	private async maybeCreateAffinitySurrogate(affinityID: string) {
		return this.affinitySurrogateInstances[affinityID] ?? (await this.createAffinitySurrogate(affinityID));
	}

	/**
	 * Create a new AffinitySurrogate instance to represent a named affinity.
	 * @param affinityID
	 */
	private async createAffinitySurrogate(affinityID: string) {
		const affinitySurrogate = new AffinitySurrogate(affinityID);

		affinitySurrogate.onEmpty = () => {
			this.removeAffinitySurrogate(affinityID);
		};

		this.affinitySurrogateInstances[affinityID] = affinitySurrogate;

		await affinitySurrogate.init();

		return affinitySurrogate;
	}

	/**
	 * Remove and close an affinity group. Note that this will cause all windows spawned
	 * by the AffinitySurrogate to also close.
	 * @param affinityID
	 */
	private removeAffinitySurrogate(affinityID: string) {
		const affinitySurrogate = this.affinitySurrogateInstances[affinityID];
		if (affinitySurrogate) {
			logger.debug(`AffinityManager - Removing affinity surrogate: ${affinityID}`);
			const { webContents } = affinitySurrogate.browserWindow;
			delete windowsIndex.byWebContentsId[webContents.id];
			affinitySurrogate.browserWindow.close();
			delete this.affinitySurrogateInstances[affinityID];
		}
	}

	/**
	 * Spawns a window with a certain affinity.
	 * This function is the entry point to spawning a window with affinity.
	 * @param affinityID
	 * @param url
	 * @param opts BrowserWindowConstructorOptions
	 */
	async spawn(affinityID: string, url: string, opts: BrowserWindowConstructorOptions): Promise<BrowserWindow> {
		const affinitySurrogate = await this.maybeCreateAffinitySurrogate(affinityID);

		return new Promise(async (resolve, reject) => {
			try {
				logger.debug(`AffinityManager - affinity id: ${affinityID} spawning window for ${url}`);
				const browserWindow = await affinitySurrogate.launchChildWindow(url, opts);

				resolve(browserWindow);
			} catch (err) {
				logger.error(`Unable to launch child window with url: ${url} and affinity id: ${affinityID}, Error: ${err}`);
				reject(err);
			}
		});
	}
}

const affinityManagerSingleton = new AffinityManager();

export { affinityManagerSingleton };
